const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

const DAY_MONTH = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
});

const DAY_MONTH_YEAR = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
});

const dateOnlyAsUtc = (value: string) => {
    const match = DATE_ONLY.exec(value);
    if (!match) return null;

    const [, year, month, day] = match;
    const date = new Date(
        Date.UTC(Number(year), Number(month) - 1, Number(day)),
    );
    return date.getUTCFullYear() === Number(year) &&
        date.getUTCMonth() === Number(month) - 1 &&
        date.getUTCDate() === Number(day)
        ? date
        : null;
};

export const parseDate = (value: string) => {
    if (DATE_ONLY.test(value)) return dateOnlyAsUtc(value);
    return new Date(value);
};

/** Returns whether a non-empty input can be parsed as a valid date. */
export const isValidDate = (value: string | null | undefined) => {
    if (!value) return false;
    const date = parseDate(value);
    return !!date && !Number.isNaN(date.getTime());
};

const formatDate = (value: string, includeYear = false) => {
    const date = parseDate(value);
    if (!date || Number.isNaN(date.getTime())) return null;
    if (DATE_ONLY.test(value))
        return (includeYear ? DAY_MONTH_YEAR : DAY_MONTH).format(date);
    return (includeYear ? DAY_MONTH_YEAR : DAY_MONTH).format(date);
};

/** Short "Mar 4" style date, or null when the input is empty or invalid. */
export const formatShortDate = (value: string | null | undefined) =>
    value ? formatDate(value) : null;

/** "Mar 4 - Mar 11" style range, collapsing to whichever valid end is present. */
export const formatDateRange = (
    start: string | null | undefined,
    end: string | null | undefined,
    includeYear = false,
) => {
    const formattedStart = start ? formatDate(start, includeYear) : null;
    const formattedEnd = end ? formatDate(end, includeYear) : null;

    if (formattedStart && formattedEnd)
        return `${formattedStart} - ${formattedEnd}`;
    return formattedStart ?? formattedEnd;
};

/** Returns calendar parts without shifting date-only values across timezones. */
export const getCalendarDateParts = (value: string | null | undefined) => {
    if (!value || !isValidDate(value)) return null;

    const date = parseDate(value);
    if (!date) return null;

    return DATE_ONLY.test(value)
        ? { year: date.getUTCFullYear(), month: date.getUTCMonth() }
        : { year: date.getFullYear(), month: date.getMonth() };
};

/** Sorts valid date values chronologically, placing invalid values last. */
export const compareDates = (
    first: string | null | undefined,
    second: string | null | undefined,
) => {
    const firstDate = first ? parseDate(first) : null;
    const secondDate = second ? parseDate(second) : null;
    const firstTime = firstDate?.getTime();
    const secondTime = secondDate?.getTime();

    if (firstTime === undefined || Number.isNaN(firstTime)) return 1;
    if (secondTime === undefined || Number.isNaN(secondTime)) return -1;
    return firstTime - secondTime;
};

/**
 * Gifts always activate at the top of the day, so the planning UI only asks for
 * a calendar date. Turns any date-like value (YYYY-MM-DD, or a value with a
 * partial time such as 2026-01-01T00:00) into an ISO instant at 00:00 local
 * time on that day. Values that are already a complete ISO datetime, or are
 * empty, are returned unchanged.
 */
const FULL_ISO_DATETIME =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

export const toStartOfDayIso = (value: string | null | undefined): string => {
    if (!value) return '';
    if (FULL_ISO_DATETIME.test(value)) return value;
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
    if (!match) return value;
    return new Date(`${match[1]}T00:00:00`).toISOString();
};

/** Rounds a date-like value to the nearest midnight UTC, returning an ISO instant. */
export const roundToClosestMidnightUtc = (
    value: string | null | undefined,
): string => {
    if (!value) return '';

    const timestamp = new Date(value).getTime();
    if (Number.isNaN(timestamp)) return value;

    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    return new Date(
        Math.round(timestamp / millisecondsPerDay) * millisecondsPerDay,
    ).toISOString();
};
